import { View } from "@amzn/stencil-react-components/layout";

/**
 * The Aiva wordmark glyph.
 *
 * The artwork is 14.667 x 10.8955 units and is centred inside a square canvas,
 * which is how it is composed in the Figma source (inset 15.96% vertically and
 * 4.16% horizontally). Colour is inherited via `currentColor` so callers control
 * it with a token-bound `color` prop rather than a hardcoded fill.
 */
export function AivaGlyph({ size = 16 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block" }}
    >
      <g transform="translate(0.6665 2.55225)">
        <path
          d="M7.33301 0C10.3817 0 12.9962 1.38198 14.1025 3.34863V1.42871C14.1027 1.27303 14.2291 1.14648 14.3848 1.14648C14.5404 1.14657 14.6669 1.27308 14.667 1.42871V5.16113H14.6562C14.6629 5.2559 14.667 5.35128 14.667 5.44727V5.79688C14.667 7.27153 14.4929 8.90797 13.251 9.70312C11.8232 10.6171 9.63517 10.8955 7.33301 10.8955C5.03079 10.8955 2.84275 10.6173 1.41504 9.70312C0.173252 8.90794 0 7.27148 0 5.79688V5.44727C8.18714e-06 5.35129 0.00315726 5.25589 0.00976562 5.16113H0V1.42871C0.000111978 1.27303 0.126523 1.14648 0.282227 1.14648C0.43785 1.14658 0.564341 1.27309 0.564453 1.42871V3.34863C1.67082 1.38212 4.28454 9.84693e-05 7.33301 0ZM3.93848 4.19043C2.68912 4.19066 1.67606 5.12835 1.67578 6.28516C1.67578 7.44218 2.68895 8.38063 3.93848 8.38086H10.7275C11.9773 8.38085 12.9902 7.44232 12.9902 6.28516C12.99 5.12821 11.9771 4.19044 10.7275 4.19043H3.93848ZM3.65625 5.76172C4.12483 5.7618 4.50488 6.11396 4.50488 6.54785C4.5047 6.98159 4.12471 7.33293 3.65625 7.33301C3.18771 7.33301 2.8078 6.98164 2.80762 6.54785C2.80762 6.11391 3.1876 5.76172 3.65625 5.76172ZM11.0107 5.76172C11.4792 5.7619 11.8594 6.11403 11.8594 6.54785C11.8592 6.98153 11.4791 7.33282 11.0107 7.33301C10.5422 7.33301 10.1623 6.98164 10.1621 6.54785C10.1621 6.11391 10.5421 5.76172 11.0107 5.76172Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
}

/**
 * Round Aiva avatar shown on the agent turn only.
 *
 * Per the design spec: 24px circle filled with pink/60, containing a 16px
 * glyph at a 4px inset. 24px matches Stencil's Avatar `xs` size, which is the
 * correct size to sit alongside a single line of accompanying text. Stencil's
 * own `Avatar` is employee-oriented (username/employeeId/profile image), so a
 * bot avatar is composed from `View` instead.
 *
 * Decorative only — the accessible sender name is rendered as adjacent text.
 */
export function AivaAvatar(): JSX.Element {
  return (
    <View
      backgroundColor="color.pink.60"
      color="color.neutral.00"
      borderRadius="dimensions.border.radius.round"
      padding="S100"
      display="flex"
      flexShrink={0}
      dataTestId="aivaAvatar"
    >
      <AivaGlyph />
    </View>
  );
}
